import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { RecentWorkCarousel } from '@/components/RecentWorkCarousel';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

export default function HomePage() {
  const servicesPreviews = [
    { title: 'Social Media Strategy & Management', desc: 'Planned, intentional, and aligned with the larger vision of your brand.' },
    { title: 'Creative Direction & Content Strategy', desc: 'Defining how your brand feels through visual language and storytelling.' },
    { title: 'VIP Content Days', desc: 'Immersive production experiences for campaign-ready assets in a single session.' },
    { title: 'Event Coverage & Experiences', desc: 'Storytelling-first documentation for launches, activations, and luxury moments.' },
  ];

  const methodSteps = [
    { num: '01', title: 'Discover' },
    { num: '02', title: 'Clarify' },
    { num: '03', title: 'Direct' },
    { num: '04', title: 'Produce' },
    { num: '05', title: 'Refine' },
    { num: '06', title: 'Expand' },
  ];

  const pillars = [
    { title: 'Strategy First', desc: 'Every decision begins with clarity.' },
    { title: 'Direction Matters', desc: 'Visuals should not feel random.' },
    { title: 'Presence Over Volume', desc: 'Build something memorable.' },
    { title: 'Creative + Business', desc: 'Content that supports growth.' },
  ];

  return (
    <div className="pt-44" data-testid="homepage">
      {/* ─── HERO ─── */}
      <section className="min-h-[80vh] flex items-center" data-testid="hero-section">
        <div className="container-main py-16 md:py-28">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto text-center">
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.12] tracking-tight mb-10">
              Elevated Brands, Thoughtfully Built.
            </motion.h1>

            <motion.div variants={fadeInUp} className="mb-10">
              <img src="/images/you-are-the-muse-hero.png" alt="You Are The Muse" className="w-full max-w-md mx-auto object-contain" data-testid="hero-image" />
            </motion.div>

            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-6">
              Muse Social Creative is a boutique agency specializing in social media strategy, elevated content production, creative direction, and luxury brand storytelling.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-12">
              Our work blends strategy with aesthetics, helping brands build a digital presence that feels cohesive, aspirational, and unmistakably aligned.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/portfolio" className="inline-flex items-center gap-2 border border-foreground text-foreground font-medium text-sm px-8 py-3.5 rounded-full hover:bg-foreground hover:text-background transition-colors duration-300" data-testid="hero-portfolio-btn">
                View Portfolio
              </Link>
              <Link to="/book-a-call" className="inline-flex items-center gap-2 bg-butter text-foreground font-medium text-sm px-8 py-3.5 rounded-full hover:bg-butter-dark transition-colors duration-300" data-testid="hero-work-btn">
                Work With Muse
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── SHOWREEL VIDEO ─── */}
      <section className="w-full" data-testid="showreel-section">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="w-full"
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="/images/muse-showreel-poster.jpg"
            className="w-full h-auto"
            data-testid="showreel-video"
          >
            <source src="/images/muse-showreel-web.mp4" type="video/mp4" />
          </video>
        </motion.div>
      </section>

      {/* ─── WHY MUSE ─── */}
      <section className="py-16 md:py-24" data-testid="why-muse-section">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4 text-center">Why Muse</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight mb-10 max-w-3xl mx-auto text-center">
              Because perception shapes value.
            </motion.h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <motion.div variants={fadeInUp} className="space-y-6">
                <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Muse Social Creative was created for brands that understand visibility is about more than content output.</p>
                <p className="text-foreground font-medium">A strong brand is not simply active online. It feels recognizable. Intentional. Aligned.</p>
                <p className="text-muted-foreground text-base md:text-lg leading-relaxed">We believe social media should not feel disconnected from your identity. It should reflect who you are, what you stand for, and how you want to be remembered.</p>
                <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Every visual, caption, campaign, and experience contributes to how your audience perceives you. That perception becomes your positioning. And positioning becomes your value.</p>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <img src="/images/what-we-do.png" alt="Muse editorial" className="w-full h-[500px] object-cover object-[center_20%]" />
              </motion.div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mt-16">
              {pillars.map((p, i) => (
                <motion.div key={i} variants={fadeInUp} data-testid={`pillar-${i}`}>
                  <h3 className="font-serif text-lg font-medium mb-2">{p.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">{p.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── SERVICES PREVIEW ─── */}
      <section className="py-16 md:py-24" data-testid="services-preview-section">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-12 text-center">What We Offer</motion.p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border">
              {servicesPreviews.map((s, i) => (
                <motion.div key={i} variants={fadeInUp} className="bg-background p-10 md:p-14" data-testid={`service-preview-${i}`}>
                  <h3 className="font-serif text-xl md:text-2xl font-medium mb-3">{s.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">{s.desc}</p>
                </motion.div>
              ))}
            </div>

            <motion.div variants={fadeInUp} className="text-center mt-12">
              <Link to="/services" className="inline-flex items-center gap-2 text-foreground font-medium text-sm border-b border-foreground pb-1 hover:border-butter hover:text-butter transition-colors duration-300" data-testid="explore-services-link">
                Explore Services <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ─── THE MUSE METHOD (SHORT) ─── */}
      <section className="py-16 md:py-24" data-testid="method-preview-section">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4 text-center">The Muse Method</motion.p>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto text-center mb-10">
              Every strong brand begins with clarity. Our methodology builds brands with depth, consistency, and purpose.
            </motion.p>

            <div className="flex flex-wrap justify-center gap-8 md:gap-12">
              {methodSteps.map((step, i) => (
                <motion.div key={i} variants={fadeInUp} className="text-center" data-testid={`method-step-${i}`}>
                  <span className="font-serif text-4xl text-butter/40 font-medium block">{step.num}</span>
                  <span className="font-serif text-base font-medium mt-1 block">{step.title}</span>
                </motion.div>
              ))}
            </div>

            <motion.div variants={fadeInUp} className="text-center mt-12">
              <Link to="/method" className="inline-flex items-center gap-2 text-foreground font-medium text-sm border-b border-foreground pb-1 hover:border-butter hover:text-butter transition-colors duration-300" data-testid="explore-method-link">
                Learn Our Process <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ─── PORTFOLIO PREVIEW ─── */}
      <section className="py-16 md:py-24" data-testid="portfolio-preview-section">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="text-center">
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Selected Work</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl font-medium leading-tight mb-8 max-w-3xl mx-auto">
              A curated collection of brands, campaigns, and visual stories.
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-12">
              Every project begins with intention. Our portfolio reflects brands we have helped shape through strategy, content, production, events, and creative direction.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/portfolio" className="inline-flex items-center gap-2 text-foreground font-medium text-sm border-b border-foreground pb-1 hover:border-butter hover:text-butter transition-colors duration-300" data-testid="view-portfolio-link">
                View Full Portfolio <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ─── TRUSTED BY ─── */}
      <section className="py-16 md:py-24" data-testid="trusted-by-section">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="text-center">
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Trusted By Brands & Founders</motion.p>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
              Muse Social Creative team has collaborated with brands, founders, experiences, and campaigns across beauty, hospitality, luxury, wellness, and lifestyle.
            </motion.p>
            <motion.div variants={fadeInUp} className="max-w-5xl mx-auto">
              <div className="flex flex-wrap items-center justify-center gap-8">
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-aroma360.jpg" alt="Aroma360 Luxury Scenting" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-hotel-collection.jpg" alt="Hotel Collection" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-miami-beach.jpg" alt="Miami Beach" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-we-the-best.jpg" alt="We The Best" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-korbel.jpg" alt="Korbel California Champagne" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-empowerher.png" alt="EmpowerHER" className="max-h-full max-w-full object-contain" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-yuzu.png" alt="Yuzu" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-32 w-64 flex items-center justify-center"><img src="/images/logo-sea-salt.jpg" alt="Sea and Salt Spa" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-sc.png" alt="SC" className="max-h-full max-w-full object-contain" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-ca.png" alt="CA" className="max-h-full max-w-full object-contain" /></div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ─── FOUNDER PREVIEW ─── */}
      <section className="py-16 md:py-24" data-testid="founder-preview-section">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="max-w-3xl mx-auto text-center">
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Meet The Founders</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl font-medium leading-tight mb-8">
              Built by strategy. Elevated by vision.
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed mb-12">
              Muse Social Creative was founded by Imara Grant and Dean Chambers — bringing together strategy, visual storytelling, social media expertise, and creative production.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/about" className="inline-flex items-center gap-2 text-foreground font-medium text-sm border-b border-foreground pb-1 hover:border-butter hover:text-butter transition-colors duration-300" data-testid="meet-founders-link">
                Meet The Founders <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-16 md:py-24 bg-foreground" data-testid="final-cta-section">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="max-w-3xl mx-auto text-center">
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight text-background mb-8">
              Ready To Build Something Worth Remembering?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-background/60 text-base md:text-lg leading-relaxed mb-4">
              Whether you are launching, refining, or repositioning your brand, Muse creates the strategy, direction, and visual presence needed to stand apart.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-background/80 font-medium text-base md:text-lg mb-12">
              Your audience already sees content every day. The question is: Will they remember yours?
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/book-a-call" className="inline-flex items-center gap-2 bg-butter text-foreground font-medium text-sm px-8 py-3.5 rounded-full hover:bg-butter-dark transition-colors duration-300" data-testid="cta-project-btn">
                Start Your Project <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/book-a-call" className="inline-flex items-center gap-2 border border-background/30 text-background font-medium text-sm px-8 py-3.5 rounded-full hover:bg-background hover:text-foreground transition-colors duration-300" data-testid="cta-call-btn">
                Book A Discovery Call
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
